import React, { useReducer } from 'react';
import AppointmentContext from './appointmentContext';
import AppointmentReducer from './appointmentReducer';
import api from '../../api/api';
import { ApiV2 } from '../../api/apiv2';
import {
    GET_APPOINTMENTS,
    CREATE_APPOINTMENT,
    GET_APPOINTMENT,
    DELETE_APPOINTMENT,
    UPDATE_APPOINTMENT,
    CLEAR_STATE,
    SET_LOADING,
    GET_APPOINTMENTS_BY_USER,
    GET_APPOINTMENTS_BY_STORE,
    GET_APPOINTMENTS_AR,
    CLEAR_SNACKBARS,
} from '../types';
import { HEADERS } from '../../constants/headers';
import { handleError } from '../utils/handleError';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

const AppointmentState = (props) => {
    const initialState = {
        appointments: [],
        appointment: {},
        loading: false,
        error: null,
        success: null,
    };

    const [state, dispatch] = useReducer(AppointmentReducer, initialState);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const messageAlertFunction = (message) => {
        enqueueSnackbar(message, { variant: 'error' });
    };

    //Get Appointments
    const getAppointments = async () => {
        setLoading();
        try {
            const res = await api.get(`/appointments?status=true`, HEADERS());

            dispatch({ type: GET_APPOINTMENTS, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Appointments By User
    const getAppointmentsByUser = async (userId, query) => {
        if (!query) query = '';
        setLoading();
        try {
            const res = await api.get(
                `/appointments/admin?status=true&user=${userId}${query}`,
                HEADERS()
            );
            dispatch({
                type: GET_APPOINTMENTS_BY_USER,
                payload: res.data.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Appointments By Store
    const getAppointmentsByStore = async (query) => {
        setLoading();
        try {
            const res = await api.get(
                `/appointments/admin?status=true${query}`,
                HEADERS()
            );
            dispatch({
                type: GET_APPOINTMENTS_BY_STORE,
                payload: res.data.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Appointment
    const getAppointment = async (appointmentId) => {
        setLoading();
        try {
            const res = await api.get(
                `/appointments/${appointmentId}`,
                HEADERS()
            );
            dispatch({ type: GET_APPOINTMENT, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Delete Appointment
    const deleteAppointment = async (appointmentId) => {
        setLoading();
        try {
            const res = await api.delete(
                `/appointments/${appointmentId}`,
                HEADERS()
            );
            dispatch({ type: DELETE_APPOINTMENT, payload: res.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const createAppointmentRequest = async (appointment, endpoint) => {
        clearState();
        setLoading();
        try {
            const res = await api.post(
                endpoint,
                {
                    ...appointment,
                    endDate: appointment.allDay
                        ? appointment.startDate
                        : appointment.endDate,
                },
                HEADERS()
            );
            dispatch({
                type: CREATE_APPOINTMENT,
                payload: res.data.data,
                success: 'Appointment.Created',
            });
            setSnackbarInterval();
            return true;
        } catch (error) {
            setSnackbarInterval();
            dispatch(handleError(error));
            return false;
        }
    };

    //Create Appointment
    const createAppointment = (appointment) =>
        createAppointmentRequest(appointment, '/appointments');

    const createAssistantAppointment = (appointment) =>
        createAppointmentRequest(appointment, '/appointments/assistant');

    //Update Appointment
    const updateAppointment = async (appointment, appointmentId) => {
        setLoading();
        try {
            const res = await api.put(
                `/appointments/${appointmentId}`,
                { ...appointment },
                HEADERS()
            );
            dispatch({ type: UPDATE_APPOINTMENT, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    /**
     * Updates an appointment and related data using the V2 API endpoint
     * @param {Object} params0 - The parameters object containing the data to update
     * @param {Object} params0.leadBody - Lead data to update
     * @param {Object} params0.commentBody - Comment data to create
     * @param {Object} params0.appointmentBody - Appointment data to update
     * @param {string} params1.appointmentId - ID of the appointment to update
     * @returns {Promise<void>} Promise that resolves when update is complete
     * @throws {Error} If API call fails or validation errors occur
     */
    const updateAppointmentV2 = async (
        { leadBody, commentBody, appointmentBody },
        appointmentId
    ) => {
        setLoading();
        try {
            const res = await ApiV2.put(
                `/appointments/${appointmentId}`,
                { leadBody, commentBody, appointmentBody },
                HEADERS()
            );
            dispatch({ type: UPDATE_APPOINTMENT, payload: res.data.data });
            enqueueSnackbar(t('SnackBar.AppointmentUpdated'), {
                variant: 'success',
            });
        } catch (error) {
            dispatch(handleError(error, messageAlertFunction));
        }
    };

    const getAppointmentsV2 = async (values) => {
        setLoading();
        try {
            const res = await ApiV2.post(
                `/appointments/calendar`,
                { ...values },
                HEADERS()
            );
            dispatch({ type: GET_APPOINTMENTS, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get appointments AdvancedResults
    const getAppointmentsAR = async (search) => {
        setLoading();
        try {
            const res = await api.get(`/appointments${search}`, HEADERS());
            dispatch({ type: GET_APPOINTMENTS_AR, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Clear State
    const clearState = () => dispatch({ type: CLEAR_STATE });

    //Set Loading
    const setLoading = () => dispatch({ type: SET_LOADING });

    const clearSnackbars = () =>
        dispatch({
            type: CLEAR_SNACKBARS,
        });

    const setSnackbarInterval = () => setTimeout(() => clearSnackbars(), 1000);

    return (
        <AppointmentContext.Provider
            value={{
                loading: state.loading,
                appointments: state.appointments,
                appointment: state.appointment,
                error: state.error,
                success: state.success,
                getAppointments,
                createAppointment,
                createAssistantAppointment,
                getAppointment,
                deleteAppointment,
                updateAppointment,
                clearState,
                setLoading,
                getAppointmentsByUser,
                getAppointmentsByStore,
                getAppointmentsAR,
                updateAppointmentV2,
                getAppointmentsV2
            }}
        >
            {props.children}
        </AppointmentContext.Provider>
    );
};

export default AppointmentState;
